// src/components/FormSettings/FormSubmitSettings/SystemFieldGroupsAccordion.jsx
import React, { useMemo, memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Switch,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

function SystemFieldGroupsAccordion({
  groups = {},
  activeIds,
  onToggle,
  defaultExpanded = [],
  loading = false,
  error = null,
  title = 'System fields',
}) {
  const groupEntries = useMemo(() => Object.entries(groups || {}), [groups]);
  const hasContent = groupEntries.length > 0;

  const isActive = useCallback(
    (id) => (activeIds instanceof Set ? activeIds.has(id) : false),
    [activeIds]
  );

  const handleKeyToggle = useCallback(
    (e, id, next) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onToggle?.(id, next);
      }
    },
    [onToggle]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          Loading system fields…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ p: 1, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
        {error}
      </Alert>
    );
  }

  if (!hasContent) {
    return (
      <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', py: 0.5 }}>
        No system fields available for this form.
      </Typography>
    );
  }

  return (
    <Box>
      {groupEntries.map(([groupId, group]) => {
        const expandedByDefault = defaultExpanded.includes(groupId);
        const items = group?.items || [];

        return (
		<Accordion
		  key={groupId}
		  disableGutters
		  square
		  defaultExpanded={expandedByDefault}
		  sx={{
		    // 🔹 Flat look: no borders, no radius, no background, no shadow
		    bgcolor: 'transparent',
		    border: 'none',
		    boxShadow: 'none',
		    borderRadius: 0,
		    m: 0, // no extra spacing
		    '&::before': { display: 'none' },

		    // 🔹 Divider between accordions (matches row divider)
		    '&:not(:last-of-type)': {
		      borderBottom: '1px solid #f0f0f0',
		    },
		  }}
		>

            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />}
              sx={{
                // 🔹 Align with your rows (which use 0.75rem text & ~12px vertical rhythm)
                minHeight: 30,
                px: 0, // align left edge exactly with list content
                '& .MuiAccordionSummary-content': {
                  my: 0, // remove default vertical padding
                },
                '& .MuiAccordionSummary-content.Mui-expanded': {
                  my: 0,
                },
                // keep the expand icon at the far right naturally
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: 'text.secondary', // subtle separation from row text
                }}
              >
                {group?.label ?? title}
              </Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ py: 0.25, px: 0 }}>
              {items.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ fontSize: '0.75rem', color: 'text.secondary', py: 0.25 }}
                >
                  No fields in this group.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {items.map((item) => {
                    const checked = isActive(item.id);
                    return (
                      <ListItem
                        key={item.id}
                        disableGutters
                        // 🔹 Flat list: no divider line; use tiny vertical rhythm to match your rows
                        sx={{
                          py: 0.25,
                          minHeight: 28,
                          pr: 0, // keep the switch close to the right edge
                          '& .MuiListItemSecondaryAction-root': { right: 0 },
                        }}
                        secondaryAction={
                          <Switch
                            edge="end"
                            size="small"
                            checked={checked}
                            disabled={!!item.disabled}
                            inputProps={{
                              'aria-label': `Toggle ${item.label}`,
                              'data-testid': `sys-field-toggle-${item.id}`,
                            }}
                            onChange={(e) => onToggle?.(item.id, e.target.checked)}
                          />
                        }
                      >
                        <ListItemText
                          primaryTypographyProps={{
                            variant: 'body2',
                            sx: { fontSize: '0.75rem', color: 'text.secondary' },
                          }}
                          primary={item.label}
                          onKeyDown={(e) => handleKeyToggle(e, item.id, !checked)}
                          tabIndex={0}
                          role="button"
                          aria-pressed={checked}
                          aria-label={`${item.label}, ${checked ? 'on' : 'off'}`}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}

SystemFieldGroupsAccordion.propTypes = {
  groups: PropTypes.objectOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
          disabled: PropTypes.bool,
        })
      ).isRequired,
    })
  ),
  activeIds: PropTypes.instanceOf(Set),
  onToggle: PropTypes.func.isRequired,
  defaultExpanded: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
  error: PropTypes.string,
  title: PropTypes.string,
};

export default memo(SystemFieldGroupsAccordion);
